/**
 * Venue events, as stored in the `events` Firestore collection.
 *
 * Ported from iOS `Event.swift`. Unlike iOS — which hand-parses the Firestore REST typed-value
 * wrappers (`stringValue`, `arrayValue.values`, `mapValue.fields`) — `@angular/fire` hands us
 * already-decoded native objects, so these interfaces are the plain shapes `doc.data()` yields.
 * Same convention as `reel.model.ts`.
 *
 * NAMING: keys are camelCase, matching the Firestore field names EXACTLY. Where iOS and Firestore
 * disagree, Firestore wins and the divergence is called out below — the iOS model has two fields it
 * has never actually read.
 *
 * NULLABILITY: every field is optional, and a present field may still be `null` — the dashboard
 * writes explicit nulls rather than omitting keys. `null` and absent mean the same thing to every
 * consumer here, hence `?:` plus `| null` throughout.
 *
 * TEXT IS NOT LOCALIZED. Verified against all 283 documents in both projects: `title` and
 * `description` are plain strings, never `{langCode: text}` maps. Do NOT run them through
 * `LanguageService.localizeMap` — events are single-language as authored, unlike the promotion and
 * notification docs that service's comment groups them with.
 */

/**
 * A Firestore timestamp as the compat SDK decodes it. Declared structurally rather than importing
 * `firebase.firestore.Timestamp` so this model stays free of the SDK — `toDate()` is all we use.
 * Read it through `eventDateValue()` in `event-utils.ts`, which also tolerates the `Date` and
 * ISO-string forms a cached / hand-built object can carry.
 */
export interface FirestoreTimestampLike {
  toDate(): Date;
}

/** `eventDate.startDate` / `endDate` as they can reach us. */
export type EventTimestamp = FirestoreTimestampLike | Date | string;

/** One ticket tier. A single event carries 0..n of these. */
export interface EventPrice {
  /** Integer or double in Firestore; the SDK decodes both to a JS number. */
  price?: number | null;
  description?: string | null;
}

/**
 * Where the event happens.
 *
 * `city` and `countryISO` are dead in practice — neither is populated on ANY location in either
 * project. `locationLabel()` in `event-utils.ts` falls back `city → place → country` so the
 * subtitle is not permanently blank (it is on iOS today, which reads `city` alone).
 */
export interface EventLocation {
  address?: string | null;
  /** Short area within the address, e.g. a neighbourhood. Populated on ~2/3 of locations. */
  place?: string | null;
  city?: string | null;
  country?: string | null;
  countryISO?: string | null;
  latitude?: number | null;
  longitude?: number | null;
}

/** One image or video on the event. May be absent entirely — 7 dev docs have an empty array. */
export interface EventMediaItem {
  mediaUrl?: string | null;
  mediaThumbnailUrl?: string | null;
  /** "video" (case-insensitive) => video; anything else, absent, or null => image. */
  mediaType?: string | null;
  /** "portrait" (or absent) => portrait; "landscape" => landscape. */
  orientation?: string | null;
  itemWidth?: number | null;
  itemHeight?: number | null;
}

/** Start / end of the event as calendar days. `endDate` is set on multi-day events only. */
export interface EventDate {
  startDate?: EventTimestamp | null;
  endDate?: EventTimestamp | null;
  /**
   * Firestore spells this `repetitive`; iOS declares `isRepetitive` and therefore always decodes
   * nil. Nothing reads it yet — kept for shape fidelity.
   */
  repetitive?: boolean | null;
}

/** Wall-clock times as free-text "HH:mm" (24h). `endTime` is set on a minority of events. */
export interface EventTime {
  startTime?: string | null;
  endTime?: string | null;
}

/** What the "Book now" button does. Absent on events that take no booking. */
export interface EventBookAction {
  /** "call" | "navigationLink". Anything else (or absent) renders no button. */
  type?: string | null;
  /** A phone number for `call`, a URL for `navigationLink`. */
  value?: string | null;
  /** Overrides the default "Book now" label when present. */
  title?: string | null;
}

/** A pointer to another feed item. Never populated in either project — see `relatedFeedItems`. */
export interface RelatedFeedItem {
  id?: string | null;
  type?: string | null;
}

/**
 * One event document.
 *
 * Field-name divergences from iOS `Event.swift`, all confirmed against live data:
 *  - `businessBannerUrl` — iOS declares `bussinessBannerUrl` (typo) and so has never read it.
 *  - `outdoorEvent` — the field that actually carries the boolean. iOS reads `isOutdoorEvent`,
 *    which is null on every document. Both are declared here; prefer `outdoorEvent`.
 */
export interface EventDoc {
  /** The `id` FIELD, which the details query filters on — not the document id. */
  id?: string | null;

  /** A STRING in Firestore ("316"), while the API's `clientData.id` is a NUMBER. See EventsService. */
  businessId?: string | null;
  businessName?: string | null;
  businessLogoUrl?: string | null;
  businessBannerUrl?: string | null;
  businessCountryISO?: string | null;

  /**
   * Who is putting the event on, when there is no venue to name — the identity a CITY event carries
   * instead of `businessName` / `businessLogoUrl`, which are always null on one (`D-6`).
   *
   * ⚠️ **The only two fields City Events adds to this model** (tracker I-21, GZ's ruling, already
   * shipped on iOS and Android). Always null on a Firestore venue event: nothing writes them there,
   * and `city-event.model.ts` is the only thing that fills them. A view that shows an identity must
   * resolve business → organiser → nothing, and must caption the organiser so a name in the venue's
   * slot cannot be read as the venue the diner is sitting in (R-4 — we are not the organiser).
   */
  organizerName?: string | null;
  organizerLogoUrl?: string | null;

  title?: string | null;
  description?: string | null;

  prices?: EventPrice[] | null;
  /** ISO code, e.g. "MKD" / "EUR". Can be an empty string or absent — render price without it. */
  currency?: string | null;

  mediaItems?: EventMediaItem[] | null;
  locations?: EventLocation[] | null;

  active?: boolean | null;
  isFeatured?: boolean | null;

  eventDate?: EventDate | null;
  eventTime?: EventTime | null;
  bookAction?: EventBookAction | null;

  phoneNumber?: string | null;
  eventWebUrl?: string | null;

  /** The reliable discriminator — "event" on ~97% of docs. `type` is absent on most. */
  feedItemType?: string | null;
  type?: string | null;
  category?: string | null;
  ageSuitability?: string | null;

  /** The real outdoor flag. */
  outdoorEvent?: boolean | null;
  /** Always null; iOS reads this one. Kept so the divergence is visible at the type level. */
  isOutdoorEvent?: boolean | null;

  /**
   * Drives the promotion badge. Never populated in either project, so the badge has never rendered
   * on any platform — the code path exists and self-hides.
   */
  relatedVoucherIDs?: string[] | null;
  /**
   * Drives the "Related events" carousel. Never populated in either project — same story.
   */
  relatedFeedItems?: RelatedFeedItem[] | null;
  skillIDs?: string[] | null;

  /** Set on events ingested from Instagram (dev only so far). Not surfaced in the UI. */
  igSourcePostId?: string | null;
}

/**
 * Events fields on the client/venue object, from `api/v3/shop/get-for-app/{id}`.
 *
 * `clientData` is untyped `any` at runtime — this interface exists for type-safety in the events
 * code only, matching how `AttractionsClientFields` is used.
 */
export interface EventsClientFields {
  /**
   * When true the venue opts into a city-wide feed: its own events lead the featured strip and
   * every venue's events fill the list below. When false (the common case) both sections stay
   * scoped to this venue, split by `isFeatured`. See `EventsListComponent.loadInitial`.
   */
  are_all_events_enabled?: boolean;
}

/**
 * One page of events. Firestore's JS SDK has no `offset()` — pagination is cursor-based, so the
 * caller hands the previous page's `cursor` back to fetch the next.
 */
export interface EventsPage {
  events: EventDoc[];
  /**
   * Opaque cursor for the next page (the last document snapshot). `null` once the stream is
   * exhausted, which is also how the caller knows to stop asking.
   */
  cursor: unknown | null;
}

/**
 * Documents per request. iOS is inconsistent here — its query sends `limit: 10` while the
 * "should I fetch more?" guard tests `count % 5 == 0`, so paging only works by coincidence. One
 * constant drives both sides on web.
 */
export const EVENTS_PAGE_SIZE = 10;
